/**
 * Guest CRM screen (M6) — an actionable hospitality CRM.
 *
 * Transforms the plain guest table into an operator-focused CRM that surfaces
 * guest value, upcoming stays and engagement opportunities, layered top-down:
 *
 *   1. KPI cards — Total Guests · Repeat Guests · Upcoming Guests ·
 *      High-Value/VIP Guests · Upcoming Occasions. Every metric is DERIVED from
 *      existing confirmed-reservation data (never fabricated). "Upcoming
 *      Occasions" (birthdays/anniversaries) is shown as UNAVAILABLE because the
 *      schema does not store those dates.
 *   2. Engagement Opportunities — compact actionable rows (returning guest
 *      arriving soon, VIP arriving soon, recent checkout follow-up, lapsed
 *      high-value guest) with a contextual action that reuses the EXISTING
 *      lifecycle-message capability (FR-6.7) via a confirm step. No new
 *      messaging backend, no auto-sent benefits.
 *   3. Tabs — Guests (default) · Engagement · Segments.
 *      - Guests: an enriched directory (tier, last/next stay, stays, LTV,
 *        source, tags, consent) with search + filters; clicking a guest opens a
 *        right-side profile drawer (Overview · Stay History · Preferences ·
 *        Engagement · Notes) instead of navigating away.
 *      - Engagement: opportunities grouped by Today · Next 7 days · Next 30 days
 *        with hospitality treatment suggestions (recommendations only).
 *      - Segments: dynamic guest groups (VIP, repeat, first-time, upcoming,
 *        recent checkout, high LTV, opted-in/out) with counts; opening one jumps
 *        to the Guests tab filtered to that segment.
 *
 * The previous duplicate-detection and unified-inbox tools are preserved under
 * a "Tools" tab so no existing functionality is lost.
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary). Money arrives as integer-minor STRINGS and is shown via the shared
 * {@link MoneyValue} treatment / `formatMoney`. Guest "tier" (New/Returning/
 * Loyal/VIP) is computed from real stay-count + lifetime-value data — never
 * hardcoded. Birthday/anniversary fields do not exist in the model, so the UI
 * renders their structure but treats the values as unavailable.
 *
 * SSR-safe: the initial render is a pure loading state; every network call and
 * any `Date`/`window` read runs inside an effect or an event handler.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import { MoneyValue } from "../components/MoneyValue.js";
import {
  apiCrmCreateGuest,
  apiCrmDuplicates,
  apiCrmGetGuest,
  apiCrmGuestInsights,
  apiCrmGuestStays,
  apiCrmLifecycle,
  apiCrmEnsureThread,
  apiCrmMerge,
  apiCrmMessages,
  apiCrmPostMessage,
  apiCrmSetOptOut,
  apiCrmSetTags,
  apiCrmThreads,
  asError,
  formatMoney,
  type CrmGuestInsight,
  type CrmGuestProfile,
  type CrmGuestStay,
  type CrmMessage,
  type CrmThread,
  type DuplicatePair,
  type LifecycleKind,
} from "../services.js";

/** Lifetime-value amounts are denominated in INR in the demo dataset. */
const FALLBACK_CURRENCY = "INR";

// ===========================================================================
// Derived guest model (tier, occasions, opportunities) — from EXISTING data
// ===========================================================================

/** A guest tier computed from real stay-count + lifetime-value signals. */
type GuestTier = "vip" | "loyal" | "returning" | "new";

const TIER_LABEL: Record<GuestTier, string> = {
  vip: "VIP",
  loyal: "Loyal",
  returning: "Returning",
  new: "New",
};

/**
 * The lifetime-value threshold (in minor units) above which a guest is treated
 * as high-value/VIP. ₹2,00,000 = 20000000 minor. Chosen as a clear, documented
 * operator threshold over the demo dataset; VIP additionally requires a repeat
 * relationship (≥ 2 stays) so a single large booking alone is "high value" but
 * not yet "VIP".
 */
const VIP_LTV_MINOR = 20_000_000;

/** Days within which a future stay counts as "arriving soon" for engagement. */
const ARRIVING_SOON_DAYS = 30;
/** Days after checkout within which a guest is a "recent checkout". */
const RECENT_CHECKOUT_DAYS = 14;
/** Days since last stay beyond which a high-value guest is "lapsed". */
const LAPSED_DAYS = 180;

/** Parse a `YYYY-MM-DD` (or null) into a local Date at midnight, or null. */
function parseDate(iso: string | null): Date | null {
  if (iso === null || iso.length < 10) {
    return null;
  }
  const [y, m, d] = iso.slice(0, 10).split("-").map((n) => Number.parseInt(n, 10));
  if (
    y === undefined ||
    m === undefined ||
    d === undefined ||
    Number.isNaN(y) ||
    Number.isNaN(m) ||
    Number.isNaN(d)
  ) {
    return null;
  }
  return new Date(y, m - 1, d);
}

/** Whole days from `a` to `b` (b - a), using local midnight dates. */
function daysBetween(a: Date, b: Date): number {
  const ms = b.getTime() - a.getTime();
  return Math.round(ms / 86_400_000);
}

/** Format a `YYYY-MM-DD` for compact display, or an em dash when null. */
function formatDate(iso: string | null): string {
  const d = parseDate(iso);
  if (d === null) {
    return "—";
  }
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Format an ISO timestamp for compact display (date + time), guarded. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/** A displayable guest label, falling back to a short id when unnamed. */
function guestLabel(name: string | null, id: string): string {
  return name !== null && name.trim() !== "" ? name : `Guest ${id.slice(0, 8)}`;
}

/** Guest initials for the avatar tile (max two letters). */
function guestInitials(name: string | null): string {
  if (name === null || name.trim() === "") {
    return "G";
  }
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "G";
}

/**
 * Classify a guest into a tier from REAL data:
 *   - vip:       lifetime value ≥ VIP threshold AND ≥ 2 stays
 *   - loyal:     ≥ 3 stays
 *   - returning: 2 stays
 *   - new:       ≤ 1 stay (includes prospects with 0 confirmed stays)
 * A guest explicitly tagged "vip" is also treated as VIP (operator override).
 */
function guestTier(g: CrmGuestInsight): GuestTier {
  const taggedVip = g.tags.some((t) => t.toLowerCase() === "vip");
  const ltv = Number(g.lifetimeValueMinor);
  if (taggedVip || (ltv >= VIP_LTV_MINOR && g.stayCount >= 2)) {
    return "vip";
  }
  if (g.stayCount >= 3) {
    return "loyal";
  }
  if (g.stayCount === 2) {
    return "returning";
  }
  return "new";
}

/** True when the guest is high-value/VIP for KPI + opportunity purposes. */
function isHighValue(g: CrmGuestInsight): boolean {
  const tier = guestTier(g);
  return tier === "vip" || Number(g.lifetimeValueMinor) >= VIP_LTV_MINOR;
}

/** The engagement-opportunity categories we can derive from existing data. */
type OpportunityKind =
  | "returning_arriving"
  | "vip_arriving"
  | "recent_checkout"
  | "lapsed_high_value";

interface Opportunity {
  readonly key: string;
  readonly kind: OpportunityKind;
  readonly guest: CrmGuestInsight;
  readonly tier: GuestTier;
  /** The relevant date (`YYYY-MM-DD`) this opportunity hinges on. */
  readonly date: string | null;
  /** Days from today to `date` (negative = in the past). */
  readonly dayOffset: number;
  readonly reason: string;
  /** A hospitality treatment suggestion (recommendation only). */
  readonly suggestion: string;
  /** Suggested lifecycle-message kind to prefill the confirm action. */
  readonly lifecycle: LifecycleKind;
}

/**
 * Derive engagement opportunities from a guest's real stay data relative to
 * `today`. Returns the opportunities that apply to this guest (a guest can
 * appear in more than one). NO birthday/anniversary opportunities are produced
 * because those dates are not in the model.
 */
function opportunitiesFor(g: CrmGuestInsight, today: Date): Opportunity[] {
  const out: Opportunity[] = [];
  const tier = guestTier(g);
  const highValue = isHighValue(g);

  const next = parseDate(g.nextStayDate);
  if (next !== null) {
    const offset = daysBetween(today, next);
    if (offset >= 0 && offset <= ARRIVING_SOON_DAYS) {
      if (highValue) {
        out.push({
          key: `${g.id}:vip_arriving`,
          kind: "vip_arriving",
          guest: g,
          tier,
          date: g.nextStayDate,
          dayOffset: offset,
          reason: "VIP / high-value guest arriving soon",
          suggestion:
            "Personalised pre-arrival outreach and manager attention. Confirm a room/food preference is on file.",
          lifecycle: "pre_arrival",
        });
      } else if (g.stayCount >= 2) {
        out.push({
          key: `${g.id}:returning_arriving`,
          kind: "returning_arriving",
          guest: g,
          tier,
          date: g.nextStayDate,
          dayOffset: offset,
          reason: "Returning guest arriving soon",
          suggestion:
            "Welcome-back note, remembered room/food preference, priority upgrade if available.",
          lifecycle: "pre_arrival",
        });
      }
    }
  }

  const lastCheckout = parseDate(g.lastStayDate);
  if (lastCheckout !== null) {
    const sinceCheckout = daysBetween(lastCheckout, today);
    if (sinceCheckout >= 0 && sinceCheckout <= RECENT_CHECKOUT_DAYS) {
      out.push({
        key: `${g.id}:recent_checkout`,
        kind: "recent_checkout",
        guest: g,
        tier,
        date: g.lastStayDate,
        dayOffset: -sinceCheckout,
        reason: "Recently checked out — good for follow-up",
        suggestion:
          "Thank-you message, review request, or a return-stay offer.",
        lifecycle: "review_request",
      });
    } else if (highValue && sinceCheckout >= LAPSED_DAYS && next === null) {
      out.push({
        key: `${g.id}:lapsed_high_value`,
        kind: "lapsed_high_value",
        guest: g,
        tier,
        date: g.lastStayDate,
        dayOffset: -sinceCheckout,
        reason: `High-value guest — no stay in ${Math.floor(sinceCheckout / 30)} months`,
        suggestion:
          "Personalised win-back outreach; consider a returning-guest incentive.",
        lifecycle: "post_stay",
      });
    }
  }

  return out;
}

// ===========================================================================
// Small presentational pieces
// ===========================================================================

/** A guest-tier badge (New / Returning / Loyal / VIP). */
function TierBadge({ tier }: { readonly tier: GuestTier }): ReactElement {
  return (
    <span className={`crm-tier crm-tier--${tier}`}>{TIER_LABEL[tier]}</span>
  );
}

/** A marketing-consent pill (opted-in vs opted-out). */
function ConsentPill({ optOut }: { readonly optOut: boolean }): ReactElement {
  return (
    <span
      className={optOut ? "crm-optout crm-optout--yes" : "crm-optout crm-optout--no"}
    >
      {optOut ? "Opted out" : "Opted in"}
    </span>
  );
}

/** A tiny visual initials tile used in cards / opportunity rows / the drawer. */
function GuestTile({
  name,
  size = "sm",
}: {
  readonly name: string | null;
  readonly size?: "sm" | "lg";
}): ReactElement {
  return (
    <span className={`crm-tile crm-tile--${size}`} aria-hidden="true">
      {guestInitials(name)}
    </span>
  );
}

/** One action in a row overflow menu. */
interface RowMenuAction {
  readonly key: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly danger?: boolean;
}

/** A compact ⋮ overflow menu (reuses the global accounts-rowmenu styles). */
function RowMenu({
  actions,
  label = "More actions",
}: {
  readonly actions: readonly RowMenuAction[];
  readonly label?: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onDocClick(e: MouseEvent): void {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div className="accounts-rowmenu" ref={rootRef}>
      <button
        type="button"
        className="console-btn console-btn--ghost accounts-rowmenu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open ? (
        <ul className="accounts-rowmenu__menu" role="menu">
          {actions.map((a) => (
            <li key={a.key} role="none">
              <button
                type="button"
                role="menuitem"
                className={
                  a.danger
                    ? "accounts-rowmenu__item accounts-rowmenu__item--danger"
                    : "accounts-rowmenu__item"
                }
                disabled={a.disabled === true}
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
              >
                {a.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ===========================================================================
// KPI cards
// ===========================================================================

interface CrmKpis {
  readonly totalGuests: number;
  readonly repeatGuests: number;
  readonly upcomingGuests: number;
  readonly vipGuests: number;
  /** Highest lifetime value across the directory, for the VIP card hint. */
  readonly topLtvMinor: number;
  readonly currency: string;
}

function computeKpis(guests: readonly CrmGuestInsight[], today: Date): CrmKpis {
  let repeat = 0;
  let upcoming = 0;
  let vip = 0;
  let topLtv = 0;
  let currency = FALLBACK_CURRENCY;
  for (const g of guests) {
    if (g.stayCount >= 2) {
      repeat += 1;
    }
    const next = parseDate(g.nextStayDate);
    if (next !== null && daysBetween(today, next) >= 0) {
      upcoming += 1;
    }
    if (isHighValue(g)) {
      vip += 1;
    }
    const ltv = Number(g.lifetimeValueMinor);
    if (ltv > topLtv) {
      topLtv = ltv;
    }
    if (g.stayCount > 0) {
      currency = g.currency;
    }
  }
  return {
    totalGuests: guests.length,
    repeatGuests: repeat,
    upcomingGuests: upcoming,
    vipGuests: vip,
    topLtvMinor: topLtv,
    currency,
  };
}

function KpiCards({
  kpis,
  occasionsAvailable,
}: {
  readonly kpis: CrmKpis;
  readonly occasionsAvailable: boolean;
}): ReactElement {
  return (
    <div className="crm-kpis" role="list" aria-label="Guest CRM key metrics">
      <div className="console-card" role="listitem">
        <p className="console-card__label">Total guests</p>
        <p className="console-card__value">{kpis.totalGuests}</p>
        <p className="console-card__hint">In your directory</p>
      </div>
      <div className="console-card" role="listitem">
        <p className="console-card__label">Repeat guests</p>
        <p className="console-card__value">{kpis.repeatGuests}</p>
        <p className="console-card__hint">2+ confirmed stays</p>
      </div>
      <div className="console-card" role="listitem">
        <p className="console-card__label">Upcoming guests</p>
        <p className="console-card__value">{kpis.upcomingGuests}</p>
        <p className="console-card__hint">Have a future stay</p>
      </div>
      <div className="console-card console-card--gold" role="listitem">
        <p className="console-card__label">High-value / VIP</p>
        <p className="console-card__value">{kpis.vipGuests}</p>
        <p className="console-card__hint">
          Top LTV{" "}
          {kpis.topLtvMinor > 0
            ? formatMoney(kpis.topLtvMinor, kpis.currency)
            : "—"}
        </p>
      </div>
      <div className="console-card" role="listitem">
        <p className="console-card__label">Upcoming occasions</p>
        <p className="console-card__value">
          {occasionsAvailable ? "0" : "—"}
        </p>
        <p className="console-card__hint">
          {occasionsAvailable
            ? "Birthdays / anniversaries · next 30 days"
            : "Birthday / anniversary dates not captured yet"}
        </p>
      </div>
    </div>
  );
}

// ===========================================================================
// Guest profile drawer (Overview · Stay History · Preferences · Engagement · Notes)
// ===========================================================================

type DrawerTab =
  | "overview"
  | "stays"
  | "preferences"
  | "engagement"
  | "notes";

const DRAWER_TABS: readonly { readonly key: DrawerTab; readonly label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "stays", label: "Stay History" },
  { key: "preferences", label: "Preferences" },
  { key: "engagement", label: "Engagement" },
  { key: "notes", label: "Notes" },
];

/** A labelled definition row for the drawer overview. */
function DField({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): ReactElement {
  return (
    <div className="crm-drawer__field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function DrawerOverview({
  insight,
  profile,
}: {
  readonly insight: CrmGuestInsight;
  readonly profile: CrmGuestProfile | null;
}): ReactElement {
  const currency = insight.currency || FALLBACK_CURRENCY;
  return (
    <dl className="crm-drawer__grid">
      <DField label="Email">{insight.email ?? "—"}</DField>
      <DField label="Phone">{insight.phone ?? "—"}</DField>
      <DField label="Guest tier">
        <TierBadge tier={guestTier(insight)} />
      </DField>
      <DField label="Marketing consent">
        <ConsentPill optOut={insight.marketingOptOut} />
      </DField>
      <DField label="Source">
        {insight.sources.length > 0 ? insight.sources.join(", ") : "—"}
      </DField>
      <DField label="Properties visited">
        {insight.propertiesVisited.length > 0
          ? insight.propertiesVisited.join(", ")
          : "—"}
      </DField>
      <DField label="First stay">{formatDate(insight.firstStayDate)}</DField>
      <DField label="Last stay">{formatDate(insight.lastStayDate)}</DField>
      <DField label="Next stay">{formatDate(insight.nextStayDate)}</DField>
      <DField label="Total stays">{insight.stayCount}</DField>
      <DField label="Lifetime value">
        {formatMoney(Number(insight.lifetimeValueMinor), currency)}
      </DField>
      <DField label="Average booking value">
        {insight.stayCount > 0
          ? formatMoney(Number(insight.avgBookingValueMinor), currency)
          : "—"}
      </DField>
      {profile !== null ? (
        <DField label="Added to CRM">{formatDateTime(profile.createdAt)}</DField>
      ) : null}
    </dl>
  );
}

/**
 * Stay History — a real summary of the guest's confirmed stays derived from the
 * insight (first/last/next dates, stay count, per-property visits). The M6
 * service does not expose a per-reservation list to the CRM, so we present the
 * verified aggregates rather than inventing individual rows.
 */
function DrawerStays({ insight }: { readonly insight: CrmGuestInsight }): ReactElement {
  if (insight.stayCount === 0) {
    return (
      <p className="console-empty">
        No confirmed stays yet. This guest is a prospect — reach out to convert
        their first booking.
      </p>
    );
  }
  const currency = insight.currency || FALLBACK_CURRENCY;
  return (
    <div className="crm-drawer__section">
      <div className="crm-kpis crm-kpis--compact">
        <div className="console-card console-card--muted">
          <p className="console-card__label">Confirmed stays</p>
          <p className="console-card__value">{insight.stayCount}</p>
        </div>
        <div className="console-card console-card--muted">
          <p className="console-card__label">Lifetime value</p>
          <MoneyValue minor={Number(insight.lifetimeValueMinor)} currency={currency} />
        </div>
        <div className="console-card console-card--muted">
          <p className="console-card__label">Avg booking</p>
          <MoneyValue
            minor={Number(insight.avgBookingValueMinor)}
            currency={currency}
          />
        </div>
      </div>
      <dl className="crm-drawer__grid crm-drawer__grid--tight">
        <DField label="First stay">{formatDate(insight.firstStayDate)}</DField>
        <DField label="Most recent completed">
          {formatDate(insight.lastStayDate)}
        </DField>
        <DField label="Next upcoming stay">{formatDate(insight.nextStayDate)}</DField>
      </dl>
      <div className="crm-drawer__section">
        <p className="crm-drawer__subhead">Properties visited</p>
        {insight.propertiesVisited.length > 0 ? (
          <div className="crm-chips">
            {insight.propertiesVisited.map((p) => (
              <span key={p} className="console-badge">
                {p}
              </span>
            ))}
          </div>
        ) : (
          <p className="console-empty">No property recorded on stays.</p>
        )}
      </div>
      <div className="crm-drawer__section">
        <p className="crm-drawer__subhead">Booking sources</p>
        {insight.sources.length > 0 ? (
          <div className="crm-chips">
            {insight.sources.map((s) => (
              <span key={s} className="console-badge">
                {s}
              </span>
            ))}
          </div>
        ) : (
          <p className="console-empty">No source recorded.</p>
        )}
      </div>
    </div>
  );
}

/**
 * Preferences — structured hospitality fields. Only two are backed by the
 * current data model (free-text `preferences` and `dietaryNotes`); the rest
 * (room, bed, arrival, favourite property, language, special requirements) are
 * rendered as STRUCTURE with a clear "not captured yet" empty state so the
 * operator sees the intended shape without any invented values. An editable
 * form saves the two supported free-text fields plus tags.
 */
function DrawerPreferences({
  insight,
  profile,
  onSaveTags,
  busy,
}: {
  readonly insight: CrmGuestInsight;
  readonly profile: CrmGuestProfile | null;
  readonly onSaveTags: (tags: readonly string[]) => void;
  readonly busy: boolean;
}): ReactElement {
  const [tagsInput, setTagsInput] = useState(insight.tags.join(", "));
  useEffect(() => {
    setTagsInput(insight.tags.join(", "));
  }, [insight.id, insight.tags]);

  function handleSaveTags(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t !== "");
    onSaveTags(tags);
  }

  const unavailable = <span className="crm-unavailable">Not captured yet</span>;

  return (
    <div className="crm-drawer__section">
      <dl className="crm-drawer__grid crm-drawer__grid--tight">
        <DField label="General preferences">
          {profile?.preferences && profile.preferences.trim() !== ""
            ? profile.preferences
            : unavailable}
        </DField>
        <DField label="Food / dietary">
          {profile?.dietaryNotes && profile.dietaryNotes.trim() !== ""
            ? profile.dietaryNotes
            : unavailable}
        </DField>
        <DField label="Room preference">{unavailable}</DField>
        <DField label="Bed preference">{unavailable}</DField>
        <DField label="Arrival preference">{unavailable}</DField>
        <DField label="Special requirements">{unavailable}</DField>
        <DField label="Favourite property / unit">
          {insight.propertiesVisited.length === 1
            ? insight.propertiesVisited[0]
            : unavailable}
        </DField>
        <DField label="Language">{unavailable}</DField>
      </dl>
      <p className="recon-caption">
        Room / bed / arrival / language preferences are not part of the current
        guest data model — they are shown here as structure and can be added in a
        later data change. General and dietary preferences are editable on the
        guest record.
      </p>

      <div className="crm-drawer__section">
        <p className="crm-drawer__subhead">Important dates</p>
        <dl className="crm-drawer__grid crm-drawer__grid--tight">
          <DField label="Birthday">{unavailable}</DField>
          <DField label="Anniversary">{unavailable}</DField>
        </dl>
        <p className="recon-caption">
          The guest model does not store birthday or anniversary dates yet, so
          occasion-based engagement is unavailable until those fields exist.
        </p>
      </div>

      <form className="console-form" onSubmit={handleSaveTags} noValidate>
        <p className="console-form__title">Tags</p>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="crm-tags">Tags (comma-separated)</label>
            <input
              id="crm-tags"
              value={tagsInput}
              placeholder="vip, repeat, allergy-nuts"
              onChange={(e) => setTagsInput(e.target.value)}
            />
          </div>
        </div>
        <button type="submit" className="console-btn" disabled={busy}>
          {busy ? "Saving…" : "Save tags"}
        </button>
      </form>
    </div>
  );
}

function DrawerEngagement({
  insight,
  today,
  onSendLifecycle,
  onToggleOptOut,
  busy,
}: {
  readonly insight: CrmGuestInsight;
  readonly today: Date;
  readonly onSendLifecycle: (kind: LifecycleKind, channel: string) => void;
  readonly onToggleOptOut: () => void;
  readonly busy: boolean;
}): ReactElement {
  const opps = opportunitiesFor(insight, today);
  return (
    <div className="crm-drawer__section">
      {opps.length > 0 ? (
        <ul className="crm-opp-list">
          {opps.map((o) => (
            <li key={o.key} className="crm-opp">
              <div className="crm-opp__main">
                <p className="crm-opp__reason">{o.reason}</p>
                <p className="crm-opp__suggestion">{o.suggestion}</p>
              </div>
              <button
                type="button"
                className="console-btn"
                disabled={busy}
                onClick={() => onSendLifecycle(o.lifecycle, "email")}
              >
                Send message
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="console-empty">
          No time-sensitive engagement opportunity for this guest right now.
        </p>
      )}

      <div className="crm-drawer__section">
        <p className="crm-drawer__subhead">Marketing consent</p>
        <p className="crm-drawer__field">
          <ConsentPill optOut={insight.marketingOptOut} />{" "}
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={busy}
            onClick={onToggleOptOut}
          >
            {insight.marketingOptOut ? "Allow marketing" : "Opt out of marketing"}
          </button>
        </p>
        <p className="recon-caption">
          Lifecycle messages (pre-arrival, review request, thank-you) are
          transactional and always allowed. Marketing sends respect this consent
          (FR-6.10). Nothing is sent automatically — every message needs your
          confirmation.
        </p>
      </div>
    </div>
  );
}

function DrawerNotes({
  profile,
}: {
  readonly profile: CrmGuestProfile | null;
}): ReactElement {
  return (
    <div className="crm-drawer__section">
      <p className="crm-drawer__subhead">Preferences note</p>
      <p className="crm-drawer__note">
        {profile?.preferences && profile.preferences.trim() !== ""
          ? profile.preferences
          : "No preferences note recorded."}
      </p>
      <p className="crm-drawer__subhead">Dietary note</p>
      <p className="crm-drawer__note">
        {profile?.dietaryNotes && profile.dietaryNotes.trim() !== ""
          ? profile.dietaryNotes
          : "No dietary note recorded."}
      </p>
      <p className="recon-caption">
        Free-form structured notes beyond these fields are not part of the
        current guest model. Use tags on the Preferences tab to label a guest.
      </p>
    </div>
  );
}

function GuestDrawer({
  insight,
  profile,
  today,
  onClose,
  onSaveTags,
  onToggleOptOut,
  onSendLifecycle,
  busy,
}: {
  readonly insight: CrmGuestInsight;
  readonly profile: CrmGuestProfile | null;
  readonly today: Date;
  readonly onClose: () => void;
  readonly onSaveTags: (tags: readonly string[]) => void;
  readonly onToggleOptOut: () => void;
  readonly onSendLifecycle: (kind: LifecycleKind, channel: string) => void;
  readonly busy: boolean;
}): ReactElement {
  const [tab, setTab] = useState<DrawerTab>("overview");
  const currency = insight.currency || FALLBACK_CURRENCY;

  return (
    <div className="crm-drawer-root" role="dialog" aria-label="Guest profile" aria-modal="false">
      <button
        type="button"
        className="crm-drawer-scrim"
        aria-label="Close guest profile"
        onClick={onClose}
      />
      <aside className="crm-drawer">
        <header className="crm-drawer__head">
          <div className="crm-drawer__ident">
            <GuestTile name={insight.fullName} size="lg" />
            <div>
              <h3 className="crm-drawer__name">
                {guestLabel(insight.fullName, insight.id)}
              </h3>
              <p className="crm-drawer__sub">
                <TierBadge tier={guestTier(insight)} />
                <span className="crm-drawer__ltv">
                  {formatMoney(Number(insight.lifetimeValueMinor), currency)} ·{" "}
                  {insight.stayCount} stay{insight.stayCount === 1 ? "" : "s"}
                </span>
              </p>
            </div>
          </div>
          <button
            type="button"
            className="console-iconbtn crm-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="crm-drawer__tabs" role="tablist" aria-label="Guest sections">
          {DRAWER_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={
                tab === t.key
                  ? "crm-drawer__tab crm-drawer__tab--active"
                  : "crm-drawer__tab"
              }
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="crm-drawer__body">
          {tab === "overview" ? (
            <DrawerOverview insight={insight} profile={profile} />
          ) : null}
          {tab === "stays" ? <DrawerStays insight={insight} /> : null}
          {tab === "preferences" ? (
            <DrawerPreferences
              insight={insight}
              profile={profile}
              onSaveTags={onSaveTags}
              busy={busy}
            />
          ) : null}
          {tab === "engagement" ? (
            <DrawerEngagement
              insight={insight}
              today={today}
              onSendLifecycle={onSendLifecycle}
              onToggleOptOut={onToggleOptOut}
              busy={busy}
            />
          ) : null}
          {tab === "notes" ? <DrawerNotes profile={profile} /> : null}
        </div>
      </aside>
    </div>
  );
}

// ===========================================================================
// Guests directory (search + filters + table)
// ===========================================================================

/** The directory filter state. All values are "all" / "" when unset. */
interface DirectoryFilters {
  readonly search: string;
  readonly tier: GuestTier | "all";
  readonly property: string;
  readonly source: string;
  readonly repeat: "all" | "repeat" | "first";
  readonly consent: "all" | "in" | "out";
  readonly tag: string;
  readonly stayWindow: "all" | "upcoming" | "recent";
}

const EMPTY_FILTERS: DirectoryFilters = {
  search: "",
  tier: "all",
  property: "",
  source: "",
  repeat: "all",
  consent: "all",
  tag: "",
  stayWindow: "all",
};

/** Apply the directory filters to the insight list. Pure. */
function applyFilters(
  guests: readonly CrmGuestInsight[],
  f: DirectoryFilters,
  today: Date,
): CrmGuestInsight[] {
  const q = f.search.trim().toLowerCase();
  return guests.filter((g) => {
    if (q !== "") {
      const hay = `${g.fullName ?? ""} ${g.email ?? ""} ${g.phone ?? ""}`.toLowerCase();
      if (!hay.includes(q)) {
        return false;
      }
    }
    if (f.tier !== "all" && guestTier(g) !== f.tier) {
      return false;
    }
    if (f.property !== "" && !g.propertiesVisited.includes(f.property)) {
      return false;
    }
    if (f.source !== "" && !g.sources.includes(f.source)) {
      return false;
    }
    if (f.repeat === "repeat" && g.stayCount < 2) {
      return false;
    }
    if (f.repeat === "first" && g.stayCount >= 2) {
      return false;
    }
    if (f.consent === "in" && g.marketingOptOut) {
      return false;
    }
    if (f.consent === "out" && !g.marketingOptOut) {
      return false;
    }
    if (f.tag !== "" && !g.tags.some((t) => t.toLowerCase() === f.tag.toLowerCase())) {
      return false;
    }
    if (f.stayWindow === "upcoming") {
      const next = parseDate(g.nextStayDate);
      if (next === null || daysBetween(today, next) < 0) {
        return false;
      }
    }
    if (f.stayWindow === "recent") {
      const last = parseDate(g.lastStayDate);
      if (last === null || daysBetween(last, today) > RECENT_CHECKOUT_DAYS) {
        return false;
      }
    }
    return true;
  });
}

function GuestDirectory({
  guests,
  allGuests,
  filters,
  onFiltersChange,
  onSelect,
  selectedId,
}: {
  readonly guests: readonly CrmGuestInsight[];
  readonly allGuests: readonly CrmGuestInsight[];
  readonly filters: DirectoryFilters;
  readonly onFiltersChange: (next: DirectoryFilters) => void;
  readonly onSelect: (id: string) => void;
  readonly selectedId: string | null;
}): ReactElement {
  // Filter option sources are derived from the full (unfiltered) directory.
  const properties = useMemo(() => {
    const set = new Set<string>();
    for (const g of allGuests) {
      for (const p of g.propertiesVisited) {
        set.add(p);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allGuests]);
  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const g of allGuests) {
      for (const s of g.sources) {
        set.add(s);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allGuests]);
  const tags = useMemo(() => {
    const set = new Set<string>();
    for (const g of allGuests) {
      for (const t of g.tags) {
        set.add(t);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allGuests]);

  function set<K extends keyof DirectoryFilters>(
    key: K,
    value: DirectoryFilters[K],
  ): void {
    onFiltersChange({ ...filters, [key]: value });
  }

  const anyFilterActive =
    filters.search !== "" ||
    filters.tier !== "all" ||
    filters.property !== "" ||
    filters.source !== "" ||
    filters.repeat !== "all" ||
    filters.consent !== "all" ||
    filters.tag !== "" ||
    filters.stayWindow !== "all";

  return (
    <div className="crm-directory">
      <div className="crm-filters">
        <div className="console-field crm-filters__search">
          <label htmlFor="crm-search">Search guests</label>
          <input
            id="crm-search"
            type="search"
            value={filters.search}
            placeholder="Name, email or phone"
            onChange={(e) => set("search", e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="crm-f-tier">Tier</label>
          <select
            id="crm-f-tier"
            value={filters.tier}
            onChange={(e) => set("tier", e.target.value as GuestTier | "all")}
          >
            <option value="all">All tiers</option>
            <option value="vip">VIP</option>
            <option value="loyal">Loyal</option>
            <option value="returning">Returning</option>
            <option value="new">New</option>
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="crm-f-prop">Property</label>
          <select
            id="crm-f-prop"
            value={filters.property}
            onChange={(e) => set("property", e.target.value)}
          >
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="crm-f-source">Source</label>
          <select
            id="crm-f-source"
            value={filters.source}
            onChange={(e) => set("source", e.target.value)}
          >
            <option value="">All sources</option>
            {sources.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="crm-f-repeat">Repeat</label>
          <select
            id="crm-f-repeat"
            value={filters.repeat}
            onChange={(e) =>
              set("repeat", e.target.value as DirectoryFilters["repeat"])
            }
          >
            <option value="all">Everyone</option>
            <option value="repeat">Repeat only</option>
            <option value="first">First-time only</option>
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="crm-f-consent">Consent</label>
          <select
            id="crm-f-consent"
            value={filters.consent}
            onChange={(e) =>
              set("consent", e.target.value as DirectoryFilters["consent"])
            }
          >
            <option value="all">Any consent</option>
            <option value="in">Opted in</option>
            <option value="out">Opted out</option>
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="crm-f-stay">Stay</label>
          <select
            id="crm-f-stay"
            value={filters.stayWindow}
            onChange={(e) =>
              set("stayWindow", e.target.value as DirectoryFilters["stayWindow"])
            }
          >
            <option value="all">Any</option>
            <option value="upcoming">Upcoming stay</option>
            <option value="recent">Recent checkout</option>
          </select>
        </div>
        {tags.length > 0 ? (
          <div className="console-field">
            <label htmlFor="crm-f-tag">Tag</label>
            <select
              id="crm-f-tag"
              value={filters.tag}
              onChange={(e) => set("tag", e.target.value)}
            >
              <option value="">All tags</option>
              {tags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {anyFilterActive ? (
          <button
            type="button"
            className="console-btn console-btn--ghost crm-filters__clear"
            onClick={() => onFiltersChange(EMPTY_FILTERS)}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <p className="crm-directory__count">
        Showing {guests.length} of {allGuests.length} guest
        {allGuests.length === 1 ? "" : "s"}
      </p>

      {guests.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="console-table crm-table">
            <caption className="console-sr-only">Guest directory</caption>
            <thead>
              <tr>
                <th scope="col">Guest</th>
                <th scope="col">Contact</th>
                <th scope="col">Tier</th>
                <th scope="col">Last stay</th>
                <th scope="col">Next stay</th>
                <th scope="col">Stays</th>
                <th scope="col">Lifetime value</th>
                <th scope="col">Source</th>
                <th scope="col">Consent</th>
                <th scope="col">
                  <span className="console-sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {guests.map((g) => (
                <tr
                  key={g.id}
                  className={g.id === selectedId ? "crm-row--selected" : undefined}
                  aria-selected={g.id === selectedId}
                >
                  <td>
                    <button
                      type="button"
                      className="crm-guestcell"
                      onClick={() => onSelect(g.id)}
                    >
                      <GuestTile name={g.fullName} />
                      <span className="crm-guestcell__meta">
                        <span className="crm-guestcell__name">
                          {guestLabel(g.fullName, g.id)}
                        </span>
                        {g.tags.length > 0 ? (
                          <span className="crm-guestcell__tags">
                            {g.tags.slice(0, 3).join(" · ")}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </td>
                  <td className="crm-contact">
                    <span>{g.email ?? "—"}</span>
                    {g.phone !== null ? (
                      <span className="crm-contact__phone">{g.phone}</span>
                    ) : null}
                  </td>
                  <td>
                    <TierBadge tier={guestTier(g)} />
                  </td>
                  <td>{formatDate(g.lastStayDate)}</td>
                  <td>{formatDate(g.nextStayDate)}</td>
                  <td className="recon-num">{g.stayCount}</td>
                  <td className="recon-num">
                    {formatMoney(Number(g.lifetimeValueMinor), g.currency || FALLBACK_CURRENCY)}
                  </td>
                  <td>{g.sources.length > 0 ? g.sources.join(", ") : "—"}</td>
                  <td>
                    <ConsentPill optOut={g.marketingOptOut} />
                  </td>
                  <td>
                    <div className="crm-rowactions">
                      <button
                        type="button"
                        className="console-btn console-btn--ghost"
                        onClick={() => onSelect(g.id)}
                      >
                        View
                      </button>
                      <RowMenu
                        label={`Actions for ${guestLabel(g.fullName, g.id)}`}
                        actions={[
                          { key: "view", label: "View guest", onClick: () => onSelect(g.id) },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="console-empty">
          {allGuests.length === 0
            ? "No guests yet — add one to start building your directory."
            : "No guests match these filters."}
        </p>
      )}
    </div>
  );
}

// ===========================================================================
// Engagement Opportunities (compact rows; grouped by time window)
// ===========================================================================

function OpportunityRow({
  opp,
  onView,
  onSend,
  busy,
}: {
  readonly opp: Opportunity;
  readonly onView: (id: string) => void;
  readonly onSend: (opp: Opportunity) => void;
  readonly busy: boolean;
}): ReactElement {
  const currency = opp.guest.currency || FALLBACK_CURRENCY;
  return (
    <li className="crm-opp">
      <GuestTile name={opp.guest.fullName} />
      <div className="crm-opp__main">
        <p className="crm-opp__title">
          <button
            type="button"
            className="crm-rowbtn"
            onClick={() => onView(opp.guest.id)}
          >
            {guestLabel(opp.guest.fullName, opp.guest.id)}
          </button>{" "}
          <TierBadge tier={opp.tier} />
        </p>
        <p className="crm-opp__reason">{opp.reason}</p>
        <p className="crm-opp__suggestion">{opp.suggestion}</p>
        <p className="crm-opp__facts">
          {opp.date !== null ? <span>{formatDate(opp.date)}</span> : null}
          <span>{formatMoney(Number(opp.guest.lifetimeValueMinor), currency)} LTV</span>
          {opp.guest.propertiesVisited.length > 0 ? (
            <span>{opp.guest.propertiesVisited[0]}</span>
          ) : null}
        </p>
      </div>
      <div className="crm-opp__actions">
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={() => onView(opp.guest.id)}
        >
          View guest
        </button>
        <button
          type="button"
          className="console-btn"
          disabled={busy}
          onClick={() => onSend(opp)}
        >
          Send message
        </button>
      </div>
    </li>
  );
}

/** The full Engagement tab: opportunities grouped by Today / 7 days / 30 days. */
function EngagementTab({
  opportunities,
  onView,
  onSend,
  busy,
}: {
  readonly opportunities: readonly Opportunity[];
  readonly onView: (id: string) => void;
  readonly onSend: (opp: Opportunity) => void;
  readonly busy: boolean;
}): ReactElement {
  const groups = useMemo(() => {
    const today: Opportunity[] = [];
    const week: Opportunity[] = [];
    const month: Opportunity[] = [];
    for (const o of opportunities) {
      const off = Math.abs(o.dayOffset);
      if (off === 0) {
        today.push(o);
      } else if (off <= 7) {
        week.push(o);
      } else {
        month.push(o);
      }
    }
    return { today, week, month };
  }, [opportunities]);

  function renderGroup(title: string, items: readonly Opportunity[]): ReactElement {
    return (
      <section className="console-panel" aria-label={title}>
        <h2 className="console-panel__title">
          {title} <span className="crm-group__count">{items.length}</span>
        </h2>
        {items.length > 0 ? (
          <ul className="crm-opp-list">
            {items.map((o) => (
              <OpportunityRow
                key={o.key}
                opp={o}
                onView={onView}
                onSend={onSend}
                busy={busy}
              />
            ))}
          </ul>
        ) : (
          <p className="console-empty">Nothing in this window.</p>
        )}
      </section>
    );
  }

  if (opportunities.length === 0) {
    return (
      <section className="console-panel">
        <p className="console-empty">
          No engagement opportunities right now. As guests approach arrival,
          check out, or lapse, actionable prompts will appear here — grouped by
          how soon they need attention.
        </p>
      </section>
    );
  }

  return (
    <div className="crm-engagement-groups">
      {renderGroup("Today", groups.today)}
      {renderGroup("Next 7 days", groups.week)}
      {renderGroup("Next 30 days", groups.month)}
      <p className="recon-caption">
        These are recommendations only. No paid benefit or message is triggered
        automatically — sending always requires your confirmation, and marketing
        consent is respected (FR-6.10).
      </p>
    </div>
  );
}

// ===========================================================================
// Segments (dynamic groups from existing data)
// ===========================================================================

interface Segment {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly count: number;
  /** The filter to apply when the operator opens this segment. */
  readonly filters: Partial<DirectoryFilters>;
}

function computeSegments(
  guests: readonly CrmGuestInsight[],
  today: Date,
): Segment[] {
  const count = (pred: (g: CrmGuestInsight) => boolean): number =>
    guests.reduce((n, g) => (pred(g) ? n + 1 : n), 0);
  const hasUpcoming = (g: CrmGuestInsight): boolean => {
    const next = parseDate(g.nextStayDate);
    return next !== null && daysBetween(today, next) >= 0;
  };
  const recentCheckout = (g: CrmGuestInsight): boolean => {
    const last = parseDate(g.lastStayDate);
    return last !== null && daysBetween(last, today) <= RECENT_CHECKOUT_DAYS;
  };
  return [
    {
      key: "vip",
      label: "VIP guests",
      description: "High lifetime value with a repeat relationship, or tagged VIP.",
      count: count((g) => guestTier(g) === "vip"),
      filters: { tier: "vip" },
    },
    {
      key: "repeat",
      label: "Repeat guests",
      description: "Two or more confirmed stays.",
      count: count((g) => g.stayCount >= 2),
      filters: { repeat: "repeat" },
    },
    {
      key: "first",
      label: "First-time guests",
      description: "One stay or none yet.",
      count: count((g) => g.stayCount < 2),
      filters: { repeat: "first" },
    },
    {
      key: "upcoming",
      label: "Upcoming guests",
      description: "Have a confirmed future stay.",
      count: count(hasUpcoming),
      filters: { stayWindow: "upcoming" },
    },
    {
      key: "recent",
      label: "Recently checked out",
      description: `Checked out in the last ${RECENT_CHECKOUT_DAYS} days.`,
      count: count(recentCheckout),
      filters: { stayWindow: "recent" },
    },
    {
      key: "highltv",
      label: "High lifetime value",
      description: `Lifetime value at or above ${formatMoney(VIP_LTV_MINOR, FALLBACK_CURRENCY)}.`,
      count: count((g) => Number(g.lifetimeValueMinor) >= VIP_LTV_MINOR),
      filters: { tier: "vip" },
    },
    {
      key: "optin",
      label: "Marketing opted in",
      description: "May receive marketing messages.",
      count: count((g) => !g.marketingOptOut),
      filters: { consent: "in" },
    },
    {
      key: "optout",
      label: "Marketing opted out",
      description: "Marketing messages are refused (FR-6.10).",
      count: count((g) => g.marketingOptOut),
      filters: { consent: "out" },
    },
  ];
}

function SegmentsTab({
  segments,
  onOpen,
}: {
  readonly segments: readonly Segment[];
  readonly onOpen: (seg: Segment) => void;
}): ReactElement {
  return (
    <div className="crm-segments">
      {segments.map((s) => (
        <div key={s.key} className="console-card crm-segment">
          <div className="crm-segment__head">
            <p className="console-card__label">{s.label}</p>
            <p className="crm-segment__count">{s.count}</p>
          </div>
          <p className="console-card__hint">{s.description}</p>
          <button
            type="button"
            className="console-btn console-btn--ghost crm-segment__open"
            onClick={() => onOpen(s)}
            disabled={s.count === 0}
          >
            Open guest list
          </button>
        </div>
      ))}
    </div>
  );
}

// ===========================================================================
// New guest form (create) — opened from the Guests tab header
// ===========================================================================

function NewGuestForm({
  onCreate,
  onCancel,
  busy,
}: {
  readonly onCreate: (input: {
    fullName?: string;
    email?: string;
    phone?: string;
    preferences?: string;
    dietaryNotes?: string;
    marketingOptOut?: boolean;
  }) => void;
  readonly onCancel: () => void;
  readonly busy: boolean;
}): ReactElement {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [preferences, setPreferences] = useState("");
  const [dietaryNotes, setDietaryNotes] = useState("");
  const [optOut, setOptOut] = useState(false);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const t = (s: string): string => s.trim();
    onCreate({
      ...(t(fullName) !== "" ? { fullName: t(fullName) } : {}),
      ...(t(email) !== "" ? { email: t(email) } : {}),
      ...(t(phone) !== "" ? { phone: t(phone) } : {}),
      ...(t(preferences) !== "" ? { preferences: t(preferences) } : {}),
      ...(t(dietaryNotes) !== "" ? { dietaryNotes: t(dietaryNotes) } : {}),
      marketingOptOut: optOut,
    });
    setFullName("");
    setEmail("");
    setPhone("");
    setPreferences("");
    setDietaryNotes("");
    setOptOut(false);
  }

  return (
    <form className="console-form crm-newguest" onSubmit={handleSubmit} noValidate>
      <p className="console-form__title">New guest</p>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="crm-new-name">Full name</label>
          <input id="crm-new-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="crm-new-email">Email</label>
          <input id="crm-new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="crm-new-phone">Phone</label>
          <input id="crm-new-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </div>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="crm-new-prefs">Preferences</label>
          <input id="crm-new-prefs" value={preferences} onChange={(e) => setPreferences(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="crm-new-diet">Dietary notes</label>
          <input id="crm-new-diet" value={dietaryNotes} onChange={(e) => setDietaryNotes(e.target.value)} />
        </div>
        <div className="console-field console-field--checkbox">
          <label htmlFor="crm-new-optout">
            <input
              id="crm-new-optout"
              type="checkbox"
              checked={optOut}
              onChange={(e) => setOptOut(e.target.checked)}
            />
            Opt out of marketing
          </label>
        </div>
      </div>
      <div className="crm-newguest__actions">
        <button type="submit" className="console-btn" disabled={busy}>
          {busy ? "Creating…" : "Create guest"}
        </button>
        <button type="button" className="console-btn console-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ===========================================================================
// Tools tab — preserved duplicate detection (FR-6.2) + unified inbox (FR-6.3)
// ===========================================================================

function DuplicatesPanel({
  pairs,
  hasSearched,
  onFind,
  onMerge,
  finding,
  mergingKey,
}: {
  readonly pairs: readonly DuplicatePair[];
  readonly hasSearched: boolean;
  readonly onFind: () => void;
  readonly onMerge: (pair: DuplicatePair) => void;
  readonly finding: boolean;
  readonly mergingKey: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Duplicate guests">
      <h2 className="console-panel__title">Duplicate detection</h2>
      <p className="recon-caption">
        Candidate duplicate pairs scored by similarity. Merging keeps Guest A as
        the survivor and folds Guest B into it. Probable matches should be
        confirmed before merging (FR-6.2).
      </p>
      <div className="recon-actionbar">
        <button
          type="button"
          className="console-btn console-btn--ghost"
          disabled={finding}
          onClick={onFind}
        >
          {finding ? "Finding…" : "Find duplicates"}
        </button>
      </div>
      {pairs.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Candidate duplicate guest pairs</caption>
          <thead>
            <tr>
              <th scope="col">Guest A (survivor)</th>
              <th scope="col">Guest B (merged)</th>
              <th scope="col">Score</th>
              <th scope="col">Classification</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {pairs.map((p) => {
              const key = `${p.guestIdA}:${p.guestIdB}`;
              const isMerging = mergingKey === key;
              return (
                <tr key={key}>
                  <td>{guestLabel(p.nameA, p.guestIdA)}</td>
                  <td>{guestLabel(p.nameB, p.guestIdB)}</td>
                  <td className="recon-num">{(p.score * 100).toFixed(0)}%</td>
                  <td>
                    <span className={`crm-class crm-class--${p.classification}`}>
                      {p.classification}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="console-btn console-btn--ghost"
                      disabled={isMerging}
                      onClick={() => onMerge(p)}
                    >
                      {isMerging ? "Merging…" : "Merge"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          {hasSearched
            ? "No duplicates found."
            : "Run a search to surface candidate duplicate pairs."}
        </p>
      )}
    </section>
  );
}

function MessageBubble({ message }: { readonly message: CrmMessage }): ReactElement {
  const isMarketing = message.kind === "marketing";
  return (
    <div className={`crm-msg crm-msg--${message.direction}`}>
      <p className="crm-msg__meta">
        <span className={`crm-author crm-author--${message.author}`}>{message.author}</span>
        <span>{message.direction}</span>
        <span className={isMarketing ? "crm-kind crm-kind--marketing" : "crm-kind"}>
          {message.kind}
        </span>
        <span className="crm-msg__time">{formatDateTime(message.sentAt)}</span>
      </p>
      <p className="crm-msg__body">{message.body}</p>
    </div>
  );
}

function InboxPanel({
  threads,
  selectedThreadId,
  selectedThread,
  messages,
  onSelect,
  replyBody,
  onReplyBodyChange,
  onSendReply,
  sending,
}: {
  readonly threads: readonly CrmThread[];
  readonly selectedThreadId: string | null;
  readonly selectedThread: CrmThread | null;
  readonly messages: readonly CrmMessage[];
  readonly onSelect: (id: string) => void;
  readonly replyBody: string;
  readonly onReplyBodyChange: (value: string) => void;
  readonly onSendReply: () => void;
  readonly sending: boolean;
}): ReactElement {
  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    onSendReply();
  }
  return (
    <section className="console-panel" aria-label="Unified inbox">
      <h2 className="console-panel__title">Unified inbox</h2>
      <p className="recon-caption">
        Every message thread across all guests, most recent first. Select a
        thread to read the conversation and reply.
      </p>
      {threads.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Message threads</caption>
          <thead>
            <tr>
              <th scope="col">Guest</th>
              <th scope="col">Channel</th>
              <th scope="col">Subject</th>
              <th scope="col">Messages</th>
              <th scope="col">Last message</th>
            </tr>
          </thead>
          <tbody>
            {threads.map((t) => (
              <tr
                key={t.id}
                className={t.id === selectedThreadId ? "crm-row--selected" : undefined}
                aria-selected={t.id === selectedThreadId}
              >
                <td>
                  <button type="button" className="crm-rowbtn" onClick={() => onSelect(t.id)}>
                    {guestLabel(t.guestName, t.guestId)}
                  </button>
                </td>
                <td>{t.channel}</td>
                <td>{t.subject ?? "—"}</td>
                <td className="recon-num">{t.messageCount}</td>
                <td>{t.lastMessageAt !== null ? formatDateTime(t.lastMessageAt) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">No threads yet.</p>
      )}

      {selectedThread !== null ? (
        <div className="crm-detail">
          <h3 className="console-panel__title">
            {guestLabel(selectedThread.guestName, selectedThread.guestId)} ·{" "}
            {selectedThread.channel}
            {selectedThread.subject !== null ? ` · ${selectedThread.subject}` : ""}
          </h3>
          {messages.length > 0 ? (
            <div className="crm-conversation" aria-label="Conversation">
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
            </div>
          ) : (
            <p className="console-empty">No messages in this thread yet.</p>
          )}
          <form className="console-form crm-reply" onSubmit={handleSubmit} noValidate>
            <p className="console-form__title">Reply</p>
            <label htmlFor="crm-reply-body" className="console-sr-only">
              Reply message
            </label>
            <textarea
              id="crm-reply-body"
              value={replyBody}
              placeholder="Type an outbound reply…"
              onChange={(e) => onReplyBodyChange(e.target.value)}
            />
            <button type="submit" className="console-btn" disabled={sending}>
              {sending ? "Sending…" : "Send"}
            </button>
          </form>
        </div>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Overview: Currently Staying + Recent Guests (from confirmed reservations)
// ===========================================================================

/** Guest tier from a stay row's aggregated guest signals (mirrors guestTier). */
function stayTier(s: CrmGuestStay): GuestTier {
  const taggedVip = s.guestTags.some((t) => t.toLowerCase() === "vip");
  const ltv = Number(s.guestLifetimeValueMinor);
  if (taggedVip || (ltv >= VIP_LTV_MINOR && s.guestStayCount >= 2)) {
    return "vip";
  }
  if (s.guestStayCount >= 3) {
    return "loyal";
  }
  if (s.guestStayCount === 2) {
    return "returning";
  }
  return "new";
}

/** True when `today` falls within a stay's half-open `[checkIn, checkOut)`. */
function isCurrentlyStaying(s: CrmGuestStay, today: Date): boolean {
  const ci = parseDate(s.checkIn);
  const co = parseDate(s.checkOut);
  if (ci === null || co === null) {
    return false;
  }
  return ci.getTime() <= today.getTime() && today.getTime() < co.getTime();
}

/** One "currently staying" card with contact + Message / View actions. */
/** Quick-template options for the current-guest message composer. */
const MESSAGE_TEMPLATES: readonly { readonly key: string; readonly label: string; readonly body: (name: string) => string }[] = [
  {
    key: "welcome",
    label: "Welcome message",
    body: (n) => `Hi ${n}, a warm welcome to your stay with us! Please let us know if there's anything we can arrange to make it perfect.`,
  },
  {
    key: "need",
    label: "Need anything?",
    body: (n) => `Hi ${n}, just checking in — is there anything you need during your stay? We're happy to help.`,
  },
  {
    key: "checkout",
    label: "Checkout reminder",
    body: (n) => `Hi ${n}, a gentle reminder that checkout is coming up. Let us know if you'd like a late checkout or help with transport.`,
  },
  { key: "custom", label: "Custom message", body: () => "" },
];

/**
 * Message composer modal for a currently-staying guest. Reuses the existing CRM
 * lifecycle-messaging capability via `onSend` (a transactional in-stay message).
 * The composer collects a channel + body with quick templates. When `onSend`
 * reports the messaging path is not connected, the parent surfaces a clear demo
 * state — this component never fabricates a "delivered" result on its own.
 */
function MessageComposer({
  stay,
  onClose,
  onSend,
  sending,
}: {
  readonly stay: CrmGuestStay;
  readonly onClose: () => void;
  readonly onSend: (channel: string, body: string) => void;
  readonly sending: boolean;
}): ReactElement {
  const name = guestLabel(stay.guestName, stay.guestId);
  const [channel, setChannel] = useState("email");
  const [template, setTemplate] = useState("welcome");
  const [body, setBody] = useState(() => MESSAGE_TEMPLATES[0]?.body(name) ?? "");

  function applyTemplate(key: string): void {
    setTemplate(key);
    const tpl = MESSAGE_TEMPLATES.find((t) => t.key === key);
    if (tpl !== undefined) {
      setBody(tpl.body(name));
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    onSend(channel, body.trim());
  }

  // Available channels: email always; SMS/WhatsApp only when a phone is on file.
  const channels =
    stay.guestPhone !== null
      ? ["email", "sms", "whatsapp"]
      : ["email"];

  return (
    <div className="console-modal-scrim" role="presentation" onClick={onClose}>
      <div
        className="console-modal console-modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-label={`Message ${name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">Message {name}</h2>
          <button
            type="button"
            className="console-iconbtn console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          <p className="recon-caption">
            Sends a transactional in-stay message through the existing CRM
            messaging capability. Nothing is sent until you press Send.
          </p>
          <form onSubmit={handleSubmit} noValidate>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="crm-msg-channel">Channel</label>
                <select
                  id="crm-msg-channel"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  {channels.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="crm-msg-template">Quick template</label>
                <select
                  id="crm-msg-template"
                  value={template}
                  onChange={(e) => applyTemplate(e.target.value)}
                >
                  {MESSAGE_TEMPLATES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-field">
              <label htmlFor="crm-msg-body">Message</label>
              <textarea
                id="crm-msg-body"
                className="crm-composer__textarea"
                value={body}
                placeholder="Type a message to the guest…"
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            <div className="console-modal__actions">
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="console-btn"
                disabled={sending || body.trim() === ""}
              >
                {sending ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/**
 * Whole nights remaining from `today` until checkout (0 when checking out
 * today). Uses the shared local-midnight date helpers, so it is SSR-safe when
 * `today` is passed from an effect.
 */
function nightsRemaining(stay: CrmGuestStay, today: Date): number {
  const co = parseDate(stay.checkOut);
  if (co === null) {
    return 0;
  }
  return Math.max(0, daysBetween(today, co));
}

/**
 * Split a free-text preferences string into short, displayable preference
 * chips (comma / semicolon separated), capped to the two most important.
 */
function preferenceChips(prefs: string | null, tags: readonly string[]): string[] {
  const fromPrefs =
    prefs === null
      ? []
      : prefs
          .split(/[,;]/)
          .map((p) => p.trim())
          .filter((p) => p !== "");
  const chips = fromPrefs.length > 0 ? fromPrefs : [...tags];
  return chips.slice(0, 2);
}

function CurrentGuestCard({
  stay,
  today,
  onView,
  onMessage,
  onAddNote,
  onViewBooking,
  onAddPreference,
}: {
  readonly stay: CrmGuestStay;
  readonly today: Date;
  readonly onView: (id: string) => void;
  readonly onMessage: (stay: CrmGuestStay) => void;
  readonly onAddNote: (stay: CrmGuestStay) => void;
  readonly onViewBooking: (stay: CrmGuestStay) => void;
  readonly onAddPreference: (stay: CrmGuestStay) => void;
}): ReactElement {
  const tier = stayTier(stay);
  const isVip = tier === "vip";
  const place =
    stay.propertyName !== null && stay.unitName !== null
      ? `${stay.propertyName} · ${stay.unitName}`
      : (stay.propertyName ?? stay.unitName ?? "—");
  const remaining = nightsRemaining(stay, today);
  const prefs = preferenceChips(stay.guestPreferences, stay.guestTags);

  return (
    <article
      className={
        isVip ? "console-card crm-current crm-current--vip" : "console-card crm-current"
      }
    >
      <header className="crm-current__head">
        <GuestTile name={stay.guestName} size="lg" />
        <div className="crm-current__ident">
          <p className="crm-current__name">
            <button
              type="button"
              className="crm-rowbtn"
              onClick={() => onView(stay.guestId)}
            >
              {guestLabel(stay.guestName, stay.guestId)}
            </button>
          </p>
          <p className="crm-current__badges">
            <TierBadge tier={tier} />
            {stay.channel !== null ? (
              <span className="console-badge">{stay.channel}</span>
            ) : null}
          </p>
        </div>
        <RowMenu
          label={`More actions for ${guestLabel(stay.guestName, stay.guestId)}`}
          actions={[
            { key: "note", label: "Add note", onClick: () => onAddNote(stay) },
            { key: "booking", label: "View booking", onClick: () => onViewBooking(stay) },
            { key: "pref", label: "Add preference", onClick: () => onAddPreference(stay) },
          ]}
        />
      </header>

      <dl className="crm-current__meta">
        <div>
          <dt>Staying at</dt>
          <dd>
            {place}
            {stay.propertyDecommissioned ? (
              <>
                {" "}
                <span className="avos-status avos-status--pending">
                  Decommissioned property
                </span>
              </>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Dates</dt>
          <dd>
            {formatDate(stay.checkIn)} → {formatDate(stay.checkOut)}
          </dd>
        </div>
        <div>
          <dt>Nights</dt>
          <dd>
            {stay.nights} ·{" "}
            <span className="crm-current__remaining">
              {remaining === 0
                ? "checks out today"
                : `${remaining} night${remaining === 1 ? "" : "s"} left`}
            </span>
          </dd>
        </div>
        <div>
          <dt>Contact</dt>
          <dd className="crm-current__contact">
            {stay.guestEmail ?? "—"}
            {stay.guestPhone !== null ? (
              <span className="crm-contact__phone">{stay.guestPhone}</span>
            ) : null}
          </dd>
        </div>
      </dl>

      {prefs.length > 0 ? (
        <div className="crm-chips crm-current__tags">
          {prefs.map((p) => (
            <span key={p} className="console-badge crm-pref-chip">
              {p}
            </span>
          ))}
        </div>
      ) : null}

      <div className="crm-current__actions">
        <button
          type="button"
          className="console-btn"
          onClick={() => onMessage(stay)}
        >
          Message
        </button>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={() => onView(stay.guestId)}
        >
          View guest
        </button>
      </div>
    </article>
  );
}

function CurrentlyStaying({
  stays,
  today,
  onView,
  onMessage,
  onAddNote,
  onViewBooking,
  onAddPreference,
}: {
  readonly stays: readonly CrmGuestStay[];
  readonly today: Date;
  readonly onView: (id: string) => void;
  readonly onMessage: (stay: CrmGuestStay) => void;
  readonly onAddNote: (stay: CrmGuestStay) => void;
  readonly onViewBooking: (stay: CrmGuestStay) => void;
  readonly onAddPreference: (stay: CrmGuestStay) => void;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Currently staying">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Currently staying</h2>
        {stays.length > 0 ? (
          <span className="crm-group__count">{stays.length}</span>
        ) : null}
      </div>
      {stays.length > 0 ? (
        <div className="crm-current-grid">
          {stays.map((s) => (
            <CurrentGuestCard
              key={s.reservationId}
              stay={s}
              today={today}
              onView={onView}
              onMessage={onMessage}
              onAddNote={onAddNote}
              onViewBooking={onViewBooking}
              onAddPreference={onAddPreference}
            />
          ))}
        </div>
      ) : (
        <div className="crm-empty-hero">
          <p className="crm-empty-hero__title">No guests currently checked in</p>
          <p className="crm-empty-hero__sub">
            Guests who are physically staying at a property right now will appear
            here so you can contact them quickly.
          </p>
        </div>
      )}
    </section>
  );
}

/** A compact recent-checkout row. */
function RecentGuestRow({
  stay,
  onView,
}: {
  readonly stay: CrmGuestStay;
  readonly onView: (id: string) => void;
}): ReactElement {
  const place =
    stay.propertyName !== null && stay.unitName !== null
      ? `${stay.propertyName} · ${stay.unitName}`
      : (stay.propertyName ?? stay.unitName ?? "—");
  return (
    <tr>
      <td>
        <button
          type="button"
          className="crm-guestcell"
          onClick={() => onView(stay.guestId)}
        >
          <GuestTile name={stay.guestName} />
          <span className="crm-guestcell__meta">
            <span className="crm-guestcell__name">
              {guestLabel(stay.guestName, stay.guestId)}
            </span>
          </span>
        </button>
      </td>
      <td>
        {place}
        {stay.propertyDecommissioned ? (
          <>
            {" "}
            <span className="avos-status avos-status--pending">
              Decommissioned property
            </span>
          </>
        ) : null}
      </td>
      <td>{formatDate(stay.checkOut)}</td>
      <td className="recon-num">{stay.guestStayCount}</td>
      <td className="recon-num">
        {formatMoney(
          Number(stay.guestLifetimeValueMinor),
          stay.currency || FALLBACK_CURRENCY,
        )}
      </td>
      <td>
        <TierBadge tier={stayTier(stay)} />
      </td>
      <td>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={() => onView(stay.guestId)}
        >
          View guest
        </button>
      </td>
    </tr>
  );
}

function RecentGuests({
  stays,
  onView,
  onViewAll,
}: {
  readonly stays: readonly CrmGuestStay[];
  readonly onView: (id: string) => void;
  readonly onViewAll: () => void;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Recent guests">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Recent guests</h2>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={onViewAll}
        >
          View all guests
        </button>
      </div>
      {stays.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="console-table">
            <caption className="console-sr-only">Recently checked-out guests</caption>
            <thead>
              <tr>
                <th scope="col">Guest</th>
                <th scope="col">Property / unit</th>
                <th scope="col">Checked out</th>
                <th scope="col">Stays</th>
                <th scope="col">Lifetime value</th>
                <th scope="col">Tier</th>
                <th scope="col">
                  <span className="console-sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {stays.map((s) => (
                <RecentGuestRow key={s.reservationId} stay={s} onView={onView} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="console-empty">No recent checkouts to show yet.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

type CrmTab = "overview" | "directory" | "engagement" | "segments" | "tools";

const CRM_TABS: readonly { readonly key: CrmTab; readonly label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "directory", label: "Guest Directory" },
  { key: "engagement", label: "Engagement" },
  { key: "segments", label: "Segments" },
  { key: "tools", label: "Tools" },
];

interface CrmData {
  readonly insights: readonly CrmGuestInsight[];
  readonly stays: readonly CrmGuestStay[];
  readonly threads: readonly CrmThread[];
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: CrmData }
  | { readonly kind: "error" };

async function loadAll(): Promise<CrmData> {
  const [insights, stays, threads] = await Promise.all([
    apiCrmGuestInsights(),
    apiCrmGuestStays(),
    apiCrmThreads(),
  ]);
  return { insights, stays, threads };
}

export default function CrmScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);

  // `today` is read once, inside an effect (SSR-safe), as a stable midnight.
  const [today, setToday] = useState<Date | null>(null);

  const [tab, setTab] = useState<CrmTab>("overview");
  const [filters, setFilters] = useState<DirectoryFilters>(EMPTY_FILTERS);
  const [showNewGuest, setShowNewGuest] = useState(false);

  // Currently-staying message composer (Overview).
  const [composerStay, setComposerStay] = useState<CrmGuestStay | null>(null);
  const [composerSending, setComposerSending] = useState(false);

  // Drawer (right-side guest profile).
  const [drawerGuestId, setDrawerGuestId] = useState<string | null>(null);
  const [drawerProfile, setDrawerProfile] = useState<CrmGuestProfile | null>(null);
  const [guestBusy, setGuestBusy] = useState(false);

  // Tools: duplicates.
  const [pairs, setPairs] = useState<readonly DuplicatePair[]>([]);
  const [hasSearchedDupes, setHasSearchedDupes] = useState(false);
  const [finding, setFinding] = useState(false);
  const [mergingKey, setMergingKey] = useState<string | null>(null);

  // Tools: inbox.
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<readonly CrmMessage[]>([]);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await loadAll();
      setState({ kind: "ready", data });
    } catch {
      setState({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    const now = new Date();
    setToday(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    let active = true;
    loadAll()
      .then((data) => {
        if (active) {
          setState({ kind: "ready", data });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // ---- Drawer profile loading ----------------------------------------------

  const openDrawer = useCallback((id: string): void => {
    setApiError(null);
    setStatus(null);
    setDrawerGuestId(id);
    setDrawerProfile(null);
    void (async () => {
      try {
        const result = await apiCrmGetGuest(id);
        const err = asError(result);
        if (err === null) {
          setDrawerProfile(result as CrmGuestProfile);
        }
      } catch {
        // The drawer still renders from the insight; profile is optional.
      }
    })();
  }, []);

  const closeDrawer = useCallback((): void => {
    setDrawerGuestId(null);
    setDrawerProfile(null);
  }, []);

  // ---- Guest mutations (create / tags / opt-out / lifecycle) ---------------

  const handleCreateGuest = useCallback(
    async (input: {
      fullName?: string;
      email?: string;
      phone?: string;
      preferences?: string;
      dietaryNotes?: string;
      marketingOptOut?: boolean;
    }): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setGuestBusy(true);
      try {
        const result = await apiCrmCreateGuest(input);
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: "Guest created.",
          why: "The new guest profile has been added to your directory.",
          next: "Open the guest to add tags or manage consent.",
        });
        setShowNewGuest(false);
        await refresh();
      } catch {
        setApiError({
          what: "Could not create the guest.",
          why: "The request to the CRM service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setGuestBusy(false);
      }
    },
    [refresh],
  );

  const handleSaveTags = useCallback(
    async (tags: readonly string[]): Promise<void> => {
      if (drawerGuestId === null) {
        return;
      }
      setApiError(null);
      setStatus(null);
      setGuestBusy(true);
      try {
        const result = await apiCrmSetTags({ guestId: drawerGuestId, tags });
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: "Tags saved.",
          why: "This guest's tags have been updated.",
          next: "No action required.",
        });
        openDrawer(drawerGuestId);
        await refresh();
      } catch {
        setApiError({
          what: "Could not save tags.",
          why: "The request to the CRM service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setGuestBusy(false);
      }
    },
    [drawerGuestId, openDrawer, refresh],
  );

  const handleToggleOptOut = useCallback(async (): Promise<void> => {
    if (drawerGuestId === null || state.kind !== "ready") {
      return;
    }
    const current = state.data.insights.find((g) => g.id === drawerGuestId);
    if (current === undefined) {
      return;
    }
    setApiError(null);
    setStatus(null);
    setGuestBusy(true);
    const next = !current.marketingOptOut;
    try {
      const result = await apiCrmSetOptOut({
        guestId: drawerGuestId,
        marketingOptOut: next,
      });
      const err = asError(result);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      setStatus({
        what: next ? "Marketing opt-out applied." : "Marketing re-enabled.",
        why: next
          ? "Outbound marketing messages to this guest will now be refused."
          : "This guest may now receive marketing messages again.",
        next: "No action required.",
      });
      await refresh();
    } catch {
      setApiError({
        what: "Could not update marketing consent.",
        why: "The request to the CRM service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setGuestBusy(false);
    }
  }, [drawerGuestId, state, refresh]);

  const handleSendLifecycle = useCallback(
    async (guestId: string, kind: LifecycleKind, channel: string): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setGuestBusy(true);
      try {
        const result = await apiCrmLifecycle({ guestId, channel, kind });
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: "Message sent.",
          why: "A transactional lifecycle message was sent (bypasses marketing opt-out, FR-6.7).",
          next: "Open the Tools tab inbox to read the thread.",
        });
        await refresh();
      } catch {
        setApiError({
          what: "Could not send the message.",
          why: "The request to the CRM service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setGuestBusy(false);
      }
    },
    [refresh],
  );

  // Confirm-before-send for engagement opportunities (no silent auto-send).
  const handleOpportunitySend = useCallback(
    (opp: Opportunity): void => {
      if (typeof window !== "undefined") {
        const ok = window.confirm(
          `Send a "${opp.lifecycle.replace("_", " ")}" message to ${guestLabel(opp.guest.fullName, opp.guest.id)}?\n\n` +
            `Suggestion: ${opp.suggestion}\n\nThis sends a transactional message now. No paid benefit is applied.`,
        );
        if (!ok) {
          return;
        }
      }
      void handleSendLifecycle(opp.guest.id, opp.lifecycle, "email");
    },
    [handleSendLifecycle],
  );

  // ---- Tools: duplicates ----------------------------------------------------

  const handleFindDuplicates = useCallback(async (): Promise<void> => {
    setApiError(null);
    setStatus(null);
    setFinding(true);
    try {
      const found = await apiCrmDuplicates();
      setPairs(found);
      setHasSearchedDupes(true);
    } catch {
      setApiError({
        what: "Could not search for duplicates.",
        why: "The request to the CRM service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setFinding(false);
    }
  }, []);

  const handleMerge = useCallback(
    async (pair: DuplicatePair): Promise<void> => {
      setApiError(null);
      setStatus(null);
      const key = `${pair.guestIdA}:${pair.guestIdB}`;
      setMergingKey(key);
      try {
        const result = await apiCrmMerge({
          survivorId: pair.guestIdA,
          mergedId: pair.guestIdB,
        });
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: "Guests merged.",
          why: `${guestLabel(pair.nameB, pair.guestIdB)} was folded into ${guestLabel(pair.nameA, pair.guestIdA)}.`,
          next: "Review the refreshed guest directory and duplicate list.",
        });
        await refresh();
        const found = await apiCrmDuplicates();
        setPairs(found);
      } catch {
        setApiError({
          what: "Could not merge the guests.",
          why: "The request to the CRM service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setMergingKey(null);
      }
    },
    [refresh],
  );

  // ---- Tools: inbox ---------------------------------------------------------

  const loadMessages = useCallback(async (threadId: string): Promise<void> => {
    try {
      const msgs = await apiCrmMessages(threadId);
      setMessages(msgs);
    } catch {
      setMessages([]);
    }
  }, []);

  const handleSelectThread = useCallback(
    (id: string): void => {
      setApiError(null);
      setStatus(null);
      setReplyBody("");
      setSelectedThreadId(id);
      void loadMessages(id);
    },
    [loadMessages],
  );

  const handleSendReply = useCallback(async (): Promise<void> => {
    if (selectedThreadId === null) {
      return;
    }
    const body = replyBody.trim();
    if (body === "") {
      setApiError({
        what: "The reply is empty.",
        why: "A message needs a body before it can be sent.",
        next: "Type a message and try again.",
      });
      return;
    }
    setApiError(null);
    setStatus(null);
    setSending(true);
    try {
      const result = await apiCrmPostMessage({
        threadId: selectedThreadId,
        direction: "outbound",
        body,
        author: "operator",
      });
      const err = asError(result);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      setReplyBody("");
      setStatus({
        what: "Reply sent.",
        why: "Your outbound message has been added to the thread.",
        next: "No action required.",
      });
      await loadMessages(selectedThreadId);
      await refresh();
    } catch {
      setApiError({
        what: "Could not send the reply.",
        why: "The request to the CRM service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setSending(false);
    }
  }, [selectedThreadId, replyBody, loadMessages, refresh]);

  // ---- Derived values -------------------------------------------------------

  const insights = state.kind === "ready" ? state.data.insights : [];
  const stays = state.kind === "ready" ? state.data.stays : [];
  const refDate = today ?? new Date();

  const kpis = useMemo(() => computeKpis(insights, refDate), [insights, refDate]);

  // Currently staying: today falls within [checkIn, checkOut). One entry per
  // guest (a guest with multiple overlapping confirmed stays is unusual, but we
  // de-duplicate defensively), soonest check-out first.
  const currentStays = useMemo(() => {
    const seen = new Set<string>();
    const out: CrmGuestStay[] = [];
    for (const s of stays) {
      if (isCurrentlyStaying(s, refDate) && !seen.has(s.guestId)) {
        seen.add(s.guestId);
        out.push(s);
      }
    }
    return out.sort((a, b) => a.checkOut.localeCompare(b.checkOut));
  }, [stays, refDate]);

  // Recent guests: most recent checkouts (checkOut <= today), latest first, one
  // row per guest, capped to a useful subset (8).
  const recentStays = useMemo(() => {
    const seen = new Set<string>();
    const out: CrmGuestStay[] = [];
    const sorted = [...stays]
      .filter((s) => {
        const co = parseDate(s.checkOut);
        return co !== null && co.getTime() <= refDate.getTime();
      })
      .sort((a, b) => b.checkOut.localeCompare(a.checkOut));
    for (const s of sorted) {
      if (!seen.has(s.guestId)) {
        seen.add(s.guestId);
        out.push(s);
      }
      if (out.length >= 8) {
        break;
      }
    }
    return out;
  }, [stays, refDate]);
  const opportunities = useMemo(() => {
    const all: Opportunity[] = [];
    for (const g of insights) {
      all.push(...opportunitiesFor(g, refDate));
    }
    // Soonest / most-recent first: smallest absolute day-offset leads.
    return all.sort((a, b) => Math.abs(a.dayOffset) - Math.abs(b.dayOffset));
  }, [insights, refDate]);
  const segments = useMemo(
    () => computeSegments(insights, refDate),
    [insights, refDate],
  );
  const filtered = useMemo(
    () => applyFilters(insights, filters, refDate),
    [insights, filters, refDate],
  );

  const drawerInsight =
    drawerGuestId !== null
      ? (insights.find((g) => g.id === drawerGuestId) ?? null)
      : null;

  const selectedThread =
    state.kind === "ready"
      ? (state.data.threads.find((t) => t.id === selectedThreadId) ?? null)
      : null;

  function openSegment(seg: Segment): void {
    setFilters({ ...EMPTY_FILTERS, ...seg.filters });
    setTab("directory");
  }

  // Open the message composer for a currently-staying guest.
  function handleStayMessage(stay: CrmGuestStay): void {
    setApiError(null);
    setStatus(null);
    setComposerStay(stay);
  }

  // Send the composed message via the EXISTING CRM messaging capability
  // (ensure the guest's thread on the chosen channel, then post an outbound
  // transactional message). Never auto-sends and never fabricates delivery: a
  // failure surfaces a clear message rather than a fake success.
  async function handleComposerSend(channel: string, body: string): Promise<void> {
    if (composerStay === null || body.trim() === "") {
      return;
    }
    const guest = composerStay;
    setComposerSending(true);
    setApiError(null);
    setStatus(null);
    try {
      const thread = await apiCrmEnsureThread({ guestId: guest.guestId, channel });
      const threadErr = asError(thread);
      if (threadErr !== null || !("id" in thread)) {
        setApiError(
          threadErr !== null
            ? toUserMessage(threadErr)
            : {
                what: "Messaging is not available.",
                why: "The messaging service did not return a thread for this guest.",
                next: "This is a demo state — no message was sent.",
              },
        );
        return;
      }
      const posted = await apiCrmPostMessage({
        threadId: thread.id,
        direction: "outbound",
        body: body.trim(),
        author: "operator",
      });
      const postErr = asError(posted);
      if (postErr !== null) {
        setApiError(toUserMessage(postErr));
        return;
      }
      setStatus({
        what: `Message sent to ${guestLabel(guest.guestName, guest.guestId)}.`,
        why: `Your ${channel} message was added to the guest's conversation.`,
        next: "Open the Tools tab inbox to see the thread.",
      });
      setComposerStay(null);
      await refresh();
    } catch {
      setApiError({
        what: "Could not send the message.",
        why: "The request to the CRM service failed (messaging may not be connected).",
        next: "This is a demo state — no message delivery is guaranteed.",
      });
    } finally {
      setComposerSending(false);
    }
  }

  // Overflow-menu secondary actions for a current guest. These reuse existing
  // surfaces where available (View booking / Add preference open the guest
  // drawer; Add note opens the drawer's Notes area) rather than inventing new
  // backends.
  function handleStayAddNote(stay: CrmGuestStay): void {
    openDrawer(stay.guestId);
    setStatus({
      what: "Notes open in the guest profile.",
      why: "Use the Notes and Preferences tabs in the guest drawer to record details.",
      next: "No message has been sent.",
    });
  }
  function handleStayViewBooking(stay: CrmGuestStay): void {
    openDrawer(stay.guestId);
  }
  function handleStayAddPreference(stay: CrmGuestStay): void {
    openDrawer(stay.guestId);
    setStatus({
      what: "Preferences open in the guest profile.",
      why: "Edit tags and preferences from the guest drawer's Preferences tab.",
      next: "No message has been sent.",
    });
  }

  return (
    <section aria-labelledby="crm-heading" data-console-screen="crm">
      <div className="accounts-header">
        <div>
          <p className="console-page-eyebrow">Guests · CRM</p>
          <h1 id="crm-heading">Guest CRM</h1>
          <p className="console-lede">
            Understand guest value, upcoming stays and engagement opportunities —
            all from your existing booking and guest data.
          </p>
        </div>
        {state.kind === "ready" ? (
          <button
            type="button"
            className="console-btn"
            onClick={() => {
              setShowNewGuest((v) => !v);
              setTab("directory");
            }}
          >
            {showNewGuest ? "Close" : "+ Add guest"}
          </button>
        ) : null}
      </div>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading guest CRM…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Guest CRM data is unavailable right now. Try refreshing in a moment.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <>
          {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}
          {status !== null ? <UserMessage message={status} severity="status" /> : null}

          <div className="crm-tabs" role="tablist" aria-label="CRM views">
            {CRM_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? "crm-tab crm-tab--active" : "crm-tab"}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.key === "engagement" && opportunities.length > 0 ? (
                  <span className="crm-tab__badge">{opportunities.length}</span>
                ) : null}
              </button>
            ))}
          </div>

          {tab === "overview" ? (
            <>
              <KpiCards kpis={kpis} occasionsAvailable={false} />
              <CurrentlyStaying
                stays={currentStays}
                today={refDate}
                onView={openDrawer}
                onMessage={handleStayMessage}
                onAddNote={handleStayAddNote}
                onViewBooking={handleStayViewBooking}
                onAddPreference={handleStayAddPreference}
              />
              <RecentGuests
                stays={recentStays}
                onView={openDrawer}
                onViewAll={() => setTab("directory")}
              />
            </>
          ) : null}

          {tab === "directory" ? (
            <>
              {showNewGuest ? (
                <NewGuestForm
                  onCreate={(input) => void handleCreateGuest(input)}
                  onCancel={() => setShowNewGuest(false)}
                  busy={guestBusy}
                />
              ) : null}
              <section className="console-panel" aria-label="Guest directory">
                <h2 className="console-panel__title">Guest directory</h2>
                <GuestDirectory
                  guests={filtered}
                  allGuests={insights}
                  filters={filters}
                  onFiltersChange={setFilters}
                  onSelect={openDrawer}
                  selectedId={drawerGuestId}
                />
              </section>
            </>
          ) : null}

          {tab === "engagement" ? (
            <EngagementTab
              opportunities={opportunities}
              onView={openDrawer}
              onSend={handleOpportunitySend}
              busy={guestBusy}
            />
          ) : null}

          {tab === "segments" ? (
            <SegmentsTab segments={segments} onOpen={openSegment} />
          ) : null}

          {tab === "tools" ? (
            <>
              <DuplicatesPanel
                pairs={pairs}
                hasSearched={hasSearchedDupes}
                onFind={() => void handleFindDuplicates()}
                onMerge={(pair) => void handleMerge(pair)}
                finding={finding}
                mergingKey={mergingKey}
              />
              <InboxPanel
                threads={state.data.threads}
                selectedThreadId={selectedThreadId}
                selectedThread={selectedThread}
                messages={messages}
                onSelect={handleSelectThread}
                replyBody={replyBody}
                onReplyBodyChange={setReplyBody}
                onSendReply={() => void handleSendReply()}
                sending={sending}
              />
            </>
          ) : null}

          {drawerInsight !== null ? (
            <GuestDrawer
              insight={drawerInsight}
              profile={drawerProfile}
              today={refDate}
              onClose={closeDrawer}
              onSaveTags={(tags) => void handleSaveTags(tags)}
              onToggleOptOut={() => void handleToggleOptOut()}
              onSendLifecycle={(kind, channel) => {
                if (drawerInsight !== null) {
                  void handleSendLifecycle(drawerInsight.id, kind, channel);
                }
              }}
              busy={guestBusy}
            />
          ) : null}

          {composerStay !== null ? (
            <MessageComposer
              stay={composerStay}
              sending={composerSending}
              onClose={() => setComposerStay(null)}
              onSend={(channel, body) => void handleComposerSend(channel, body)}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
